import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { Box, TableCell, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { StepSpan, HourSlot, BALL_SIZE, LINE_HEIGHT } from './plan-overview-dialog.types';
import { getSlotColor, getSlotState } from './plan-overview-dialog.utils';

type SpanCellProps = {
    slotIndex: number;
    span: StepSpan;
    hourSlots: HourSlot[];
    currentHourSlotIndex?: number;
    isCurrentStepRow?: boolean;
};

const centeredAbsolute = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
};

const PlanOverviewSpanCell: FC<SpanCellProps> = ({
    slotIndex,
    span,
    hourSlots,
    currentHourSlotIndex,
    isCurrentStepRow,
}) => {
    const { inSpan, skipNonWorking, isStart, isEnd, isSingle } = getSlotState(slotIndex, span, hourSlots);
    const isCurrentHour = slotIndex === currentHourSlotIndex;

    const baseSx = {
        p: 0,
        height: 28,
        borderLeft: '1px solid #f0f0f0',
        ...((isCurrentHour || isCurrentStepRow) && { backgroundColor: '#fff9c4' }),
    };

    if (!inSpan || skipNonWorking) {
        return <TableCell sx={baseSx} />;
    }

    const slotColor = getSlotColor(slotIndex, span);
    const lineLeft = isStart ? '50%' : 0;
    const lineRight = isEnd ? '50%' : 0;

    const fmtStart = formatDate(new Date(span.startTime), 'datetime');
    const fmtEnd = formatDate(new Date(span.endTime), 'datetime');
    const tooltipTitle = `${span.stepName}\n${fmtStart} - ${fmtEnd}`;

    const renderEndpoint = (type: 'start' | 'end') => {
        const extendsOutside = type === 'start' ? span.extendsBeforeView : span.extendsAfterView;
        const Icon = type === 'start' ? ChevronLeftIcon : ChevronRightIcon;

        if (extendsOutside) {
            return <Icon sx={{ ...centeredAbsolute, color: slotColor, fontSize: 20 }} />;
        }
        return (
            <Box
                sx={{
                    ...centeredAbsolute,
                    width: BALL_SIZE,
                    height: BALL_SIZE,
                    borderRadius: '50%',
                    backgroundColor: slotColor,
                }}
            />
        );
    };

    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>} arrow placement="top">
            <TableCell sx={{ ...baseSx, position: 'relative' }}>
                {isSingle ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box
                            sx={{
                                width: BALL_SIZE,
                                height: BALL_SIZE,
                                borderRadius: '50%',
                                backgroundColor: slotColor,
                            }}
                        />
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: lineLeft,
                                right: lineRight,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: LINE_HEIGHT,
                                backgroundColor: slotColor,
                            }}
                        />
                        {isStart && renderEndpoint('start')}
                        {isEnd && renderEndpoint('end')}
                    </Box>
                )}
            </TableCell>
        </Tooltip>
    );
};

export default PlanOverviewSpanCell;
